import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { SpinnerInline } from '../Spinner';
import { Button } from '../Button';
import { IconChevronDown, IconVideo } from '../Icons';
import { cn } from '../cn';


export interface ILotShowcaseThumbnailProps {
  thumbs: string[];
  setActiveImage: (i: number) => void;
  activeImage: number;
  loading?: boolean;
  direction?: 'vertical' | 'horizontal';
  videos?: string[];
  setActiveVideoIndex: Dispatch<SetStateAction<number>>;
  activeVideoIndex: number;
}

const getYoutubeThumbnail = (url: string): string => {
  if(!url) return '';
  const youtubeIdRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(youtubeIdRegex);
  const videoId = match ? match[1] : null;

  if(videoId) {
    // Using mqdefault.jpg for medium quality thumbnail (usually 320x180)
    return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
  }
  console.warn(`Could not extract YouTube ID from URL: ${url}`);
  return '';
};

export const LotShowcaseThumbnails = ({
  thumbs: propsThumbs,
  setActiveImage,
  activeImage,
  loading,
  direction = 'vertical',
  videos,
  setActiveVideoIndex,
  activeVideoIndex,
}: ILotShowcaseThumbnailProps): JSX.Element => {
  const thumbs = [
    ...propsThumbs.slice(0, 1),
    ...(videos?.length ? videos.map(getYoutubeThumbnail) : []),
    ...propsThumbs.slice(1),
  ];
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [showStartArrow, setShowStartArrow] = useState<boolean>(false);
  const [showEndArrow, setShowEndArrow] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [offset, setOffset] = useState(0);

  const numVideos = videos?.length ?? 0;

  const calculatePages = () => {
    const container = containerRef.current;
    const scrollContainer = scrollContainerRef.current;
    if(!container || !scrollContainer || !scrollContainer.firstElementChild) return;

    // Dimension depends on direction (height for vertical, width for horizontal)
    // the 48 accounts for our prev/next navs
    const containerDimension = (direction === 'vertical'
      ? container.clientHeight
      : container.clientWidth) - 48;

    // the 8 accounts for our gap
    const childSize = ((direction === 'vertical'
      ? scrollContainer.firstElementChild?.scrollHeight
      : scrollContainer.firstElementChild?.scrollWidth) || 0 )+8;

    // Avoid division by zero if childSize is 0
    if(childSize <= 8) {
      setTotalPages(1);
      setCurrentPage(0);
      return;
    }

    const possibleVisible = Math.floor(containerDimension / childSize) * 2;

    // Calculate total pages (round up to ensure we can scroll to see all content)
    const pages = Math.ceil((thumbs?.length || 0) / possibleVisible);
    setTotalPages(pages > 0 ? pages : 1);

    // Make sure current page is still valid with new thumbs count
    const effectiveCurrentPage = currentPage >= pages && pages > 0 ? pages - 1 : currentPage;
    if(pages > 0 && currentPage >= pages) {
      setCurrentPage(pages - 1);
      // Force scroll to the correct page
      scrollToPage(pages - 1, pages);
    } else {
      scrollToPage(effectiveCurrentPage, pages);
    }
  };

  // Calculate total pages and update when thumbs change
  useEffect(() => {
    if(!containerRef.current || !scrollContainerRef.current) return;

    // Calculate initially and add resize listener
    calculatePages();
    const resizeObserver = new ResizeObserver(calculatePages);
    if(containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }


    return () => {
      resizeObserver.disconnect();
    };
  }, [thumbs?.length, direction]);

  useEffect(() => {
    setShowStartArrow(currentPage > 0);
    setShowEndArrow(currentPage < totalPages - 1);
  }, [currentPage, totalPages]);


  // Scroll to a specific page
  const scrollToPage = (pageIndex: number, currentTotalPages?: number) => {
    const pages = currentTotalPages ?? totalPages; // Use passed totalPages if available
    if(!containerRef.current || !scrollContainerRef.current || !scrollContainerRef.current.firstElementChild || pageIndex < 0 || pageIndex >= pages) return;


    const container = containerRef.current;
    const scrollContainer = scrollContainerRef.current;
    // The 48 is our prev/next navs
    const pageDimension = (direction === 'vertical'
      ? container.clientHeight
      : container.clientWidth) - 48;

    // the 8 is our gap
    const childSize = ((direction === 'vertical'
      ? scrollContainer.firstElementChild?.scrollHeight
      : scrollContainer.firstElementChild?.scrollWidth) || 0 ) + 8;

    // Avoid division by zero
    if(childSize <= 8) {
      setOffset(0);
      setCurrentPage(pageIndex);
      return;
    }


    const possibleVisible = Math.floor(pageDimension / childSize) * 2;
    const targetChildIndex = possibleVisible * pageIndex;

    // Ensure the target child exists
    if(targetChildIndex >= scrollContainer.children.length) {
      // This might happen if thumbs changed rapidly, adjust to last possible element
      // Or simply don't scroll if index is out of bounds
      console.warn('Scroll target index out of bounds');
      return;
    }


    const nextFirst = scrollContainer.children[targetChildIndex] as HTMLDivElement;


    let newOffset = 0;
    if(pageIndex > 0 && nextFirst) {
      // The 28 is our nav width/height and half the gap
      const navSize = 28; // Size of nav arrow area + half gap
      newOffset = (direction === 'vertical' ? nextFirst.offsetTop : nextFirst.offsetLeft) - navSize;
    }

    setOffset(newOffset < 0 ? 0 : newOffset); // Ensure offset is not negative
    setCurrentPage(pageIndex);
  };

  // Navigate to previous/next page
  const handleNavigation = (navDirection: 'prev' | 'next') => {
    if(navDirection === 'prev' && currentPage > 0) {
      scrollToPage(currentPage - 1);
    } else if(navDirection === 'next' && currentPage < totalPages - 1) {
      scrollToPage(currentPage + 1);
    }
  };

  // Classes and styles for different directions
  const containerClasses = direction === 'vertical'
    ? 'h-full absolute transition-transform ease-in-out duration-300 p-2.5'
    : 'w-full h-full absolute transition-transform ease-in-out duration-300 p-2.5';

  const containerWidth = direction === 'vertical' ? undefined : thumbs.length / 2 * 90;

  const gridClasses = direction === 'vertical'
    ? 'grid grid-cols-2 gap-1 md:gap-2'
    : 'grid grid-rows-2 grid-flow-col-dense max-h-full gap-1 md:gap-2';

  const topArrowClasses = direction === 'vertical'
    ? 'bg-black/40 dark:bg-black/60 hover:bg-black/60 dark:hover:bg-black/70 flex items-center justify-center absolute top-0 left-0 right-0 h-6 text-white z-10 cursor-pointer transition-colors'
    : 'bg-black/40 dark:bg-black/60 hover:bg-black/60 dark:hover:bg-black/70 flex items-center justify-center absolute top-0 bottom-0 left-0 w-6 text-white z-10 cursor-pointer transition-colors';

  const bottomArrowClasses = direction === 'vertical'
    ? 'bg-black/40 dark:bg-black/60 hover:bg-black/60 dark:hover:bg-black/70 flex items-center justify-center absolute bottom-0 left-0 right-0 h-6 text-white z-10 cursor-pointer transition-colors'
    : 'bg-black/40 dark:bg-black/60 hover:bg-black/60 dark:hover:bg-black/70 flex items-center justify-center absolute top-0 bottom-0 right-0 w-6 text-white z-10 cursor-pointer transition-colors';

  const topArrowIconClasses = direction === 'vertical'
    ? 'rotate-180 h-[22px] w-[22px]'
    : 'rotate-90 h-[22px] w-[22px]';

  const bottomArrowIconClasses = direction === 'vertical'
    ? 'h-[22px] w-[22px]'
    : '-rotate-90 h-[22px] w-[22px]';

  const imageBaseClasses = cn(
    'border-1 border-transparent overflow-hidden bg-white p-px aspect-square pointer-events-auto relative select-none',
  );
  const imageSizeClasses = direction === 'vertical' ? 'w-full' : 'h-full w-auto';

  const containerStyle = direction === 'vertical'
    ? { transform: `translateY(${-offset}px)`, width: containerWidth }
    : { transform: `translateX(${-offset}px)`, width: containerWidth };

  return (
    <div className='h-full max-h-full relative overflow-hidden'>
      {loading ? <div className='justify-center items-center flex flex-col h-full w-full'>
        <SpinnerInline/>
      </div> : <>
        {showStartArrow && (
          <Button
            unstyled
            aria-label={direction === 'vertical' ? 'Scroll thumbnails up' : 'Scroll thumbnails left'}
            onPress={() => handleNavigation('prev')}
            className={topArrowClasses}
          >
            <IconChevronDown className={topArrowIconClasses}/>
          </Button>
        )}

        {/* This outer div handles the scrolling "viewport" */}
        <div className='h-full w-full overflow-hidden' ref={containerRef}>
          {/* This inner div contains all the thumbnails and moves via transform */}
          <div
            style={containerStyle}
            className={containerClasses}
          >
            <div
              ref={scrollContainerRef}
              className={gridClasses}
            >
              {thumbs && thumbs.map((thumb, i) => {
                // Determine if this thumbnail represents a video
                const isVideo = i > 0 && i < numVideos + 1;
                // Determine if this specific thumbnail is the active one
                const isActive = isVideo ? activeVideoIndex === i - 1 : (i === 0 ? activeImage === i : activeImage === i - numVideos);

                return (
                  <Button
                    onPress={() => {
                      if(isVideo) {
                        setActiveVideoIndex(i - 1);
                        setActiveImage(-1);
                      } else {
                        setActiveImage(i === 0 ? i : i - numVideos);
                        setActiveVideoIndex(-1);
                      }
                    }}
                    key={`${isVideo ? 'vid' : 'img'}-${i}`}
                    unstyled
                    className={cn(
                      imageBaseClasses,
                      imageSizeClasses,
                      isActive ? 'border-accent' : 'border-border-active',
                    )}
                  >
                    <img
                      src={thumb}
                      alt={`Thumbnail ${i + 1}${isVideo ? ' (Video)' : ''}`}
                      className='object-cover w-full h-full'
                      width={128}
                      height={128}
                      onLoad={() => {
                        calculatePages();
                      }}
                    />
                    {isVideo && (
                      <div
                        className={cn(
                          'absolute inset-0 flex items-center justify-center',
                          'bg-black/30 group-hover:bg-black/50 transition-colors'
                        )}>
                        <IconVideo className='w-6 h-6 text-white drop-shadow-md' />
                      </div>
                    )}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>

        {showEndArrow && (
          <Button
            unstyled
            aria-label={direction === 'vertical' ? 'Scroll thumbnails down' : 'Scroll thumbnails right'}
            onPress={() => handleNavigation('next')}
            className={bottomArrowClasses}
          >
            <IconChevronDown className={bottomArrowIconClasses}/>
          </Button>
        )}
      </>}
    </div>
  );
};
